import React, { useCallback } from "react";
import { sendMessageToParent } from "../../utils";
import OdometerCounter from "./OdometerCounter";
import styles from "./index.module.scss";

export type ClickType = "points" | "totalSaved";

interface MemberInfoCardNewProps {
  username?: string;
  points?: number;
  totalSaved?: number;
  onClick?: (
    type: ClickType,
    data: { points?: number; coupons?: number },
  ) => void;
}

const perks = [
  {
    value: "100",
    lines: ["Welcome Points", "Redeem 100 Points for $5 Off"],
    elementId: "member-week-welcome-points",
  },
  {
    value: "2X",
    lines: ["Points On Eligible Orders", "Every Friday, Oct-Dec"],
    elementId: "member-week-double-points",
  },
];

const MemberInfoCardNew: React.FC<MemberInfoCardNewProps> = ({
  username,
  points = 0,
  totalSaved = 0,
  onClick,
}) => {
  const handlePerkClick = useCallback(() => {
    sendMessageToParent({
      type: "pointsRules",
    });
  }, []);

  return (
    <div className={styles.memberInfoCardNew}>
      <section className={styles.card} aria-label="Member week perks">
        <picture className={styles.background}>
          <source
            media="(min-width: 768px)"
            srcSet="https://cdn.shopify.com/s/files/1/0506/5284/0124/files/membership-center_pc.jpg?v=1790649748"
          />
          <img
            src="https://cdn.shopify.com/s/files/1/0506/5284/0124/files/membership-center_m.jpg?v=1790649763"
            alt=""
          />
        </picture>

        <div className={styles.heroContent}>
          <p className={styles.eyebrow}>YOUR MEMBER PERKS</p>
          <h1 className={styles.memberName}>Hi, {username}</h1>
          <p className={styles.eventPeriod}>
            Limited-Time Extras · Every Friday, Oct-Dec
          </p>
        </div>

        <div className={styles.crown} aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
          >
            <path
              d="M4.0625 13.125H10.9375L12.8125 6.5625L9.6875 8.125L7.5 3.75L5.3125 8.125L2.1875 6.5625L4.0625 13.125Z"
              stroke="white"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2.1875 6.5625C2.70527 6.5625 3.125 6.14277 3.125 5.625C3.125 5.10723 2.70527 4.6875 2.1875 4.6875C1.66973 4.6875 1.25 5.10723 1.25 5.625C1.25 6.14277 1.66973 6.5625 2.1875 6.5625Z"
              stroke="white"
            />
            <path
              d="M7.5 3.75C8.01777 3.75 8.4375 3.33027 8.4375 2.8125C8.4375 2.29473 8.01777 1.875 7.5 1.875C6.98223 1.875 6.5625 2.29473 6.5625 2.8125C6.5625 3.33027 6.98223 3.75 7.5 3.75Z"
              stroke="white"
            />
            <path
              d="M12.8125 6.5625C13.3303 6.5625 13.75 6.14277 13.75 5.625C13.75 5.10723 13.3303 4.6875 12.8125 4.6875C12.2947 4.6875 11.875 5.10723 11.875 5.625C11.875 6.14277 12.2947 6.5625 12.8125 6.5625Z"
              stroke="white"
            />
          </svg>
        </div>

        <div className={styles.perks}>
          {perks.map((perk, index) => (
            <React.Fragment key={perk.elementId}>
              <button
                type="button"
                className={styles.perk}
                data-element-id={perk.elementId}
                onClick={handlePerkClick}
              >
                <span className={styles.perkValue}>{perk.value}</span>
                <span className={styles.perkCopy}>
                  {perk.lines.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </span>
              </button>
              {index < perks.length - 1 && (
                <span className={styles.divider} aria-hidden="true" />
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      <div className={styles.statsContainer}>
        <div
          className={styles.statItem}
          onClick={() => onClick?.("points", { points })}
        >
          <div className={styles.statValue} data-element-id="my-points">
            <OdometerCounter value={points} format="(,ddd)" theme="default" />
          </div>
          <div className={styles.statLabel} data-element-id="my-points-label">
            My Points
          </div>
        </div>

        <div className={styles.statsDivider} />

        <div
          className={styles.statItem}
          onClick={() => onClick?.("totalSaved", { points })}
        >
          <div className={styles.statValue} data-element-id="total-saved">
            $
            <OdometerCounter
              value={totalSaved}
              format="(,ddd)"
              theme="default"
            />
          </div>
          <div className={styles.statLabel} data-element-id="total-saved-label">
            Total Savings
          </div>
        </div>
      </div>
    </div>
  );
};

export const MemberInfoCardNewLoading: React.FC = () => (
  <div className={styles.memberInfoCardNew}>
    <div
      className={`${styles.card} ${styles.loadingCard}`}
      role="status"
      aria-label="Loading member information"
    >
      <span className={styles.visuallyHidden}>Loading member information</span>
      <div className={styles.loadingContent} aria-hidden="true">
        <span className={styles.loadingLineShort} />
        <span className={styles.loadingLineTitle} />
        <span className={styles.loadingLineMedium} />
      </div>
      <div className={styles.loadingPerks} aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
    </div>
  </div>
);

export default MemberInfoCardNew;
